/**
 * TextPropertyEditor
 *
 * Reusable text input component for property editors
 * SRP: Responsible ONLY for rendering a text input and handling changes
 * Used by: any property that accepts string input (label, placeholder, name, etc.)
 */

import React, { useCallback, useMemo } from 'react';

export interface TextPropertyEditorProps {
  /** Display label for the input */
  label: string;

  /** Current value */
  value: string;

  /** Called when value changes */
  onChange: (value: string) => void;

  /** Placeholder text shown when empty */
  placeholder?: string;

  /** Allow multiple lines */
  multiline?: boolean;

  /** Number of rows (if multiline) */
  rows?: number;

  /** Maximum character length */
  maxLength?: number;

  /** Field is required */
  required?: boolean;

  /** Field is disabled */
  disabled?: boolean;

  /** Help text shown below input */
  helpText?: string;

  /** Custom CSS class */
  className?: string;
}

/**
 * TextPropertyEditor component
 * Renders a simple text input for configuring string properties
 */
export const TextPropertyEditor: React.FC<TextPropertyEditorProps> = ({
  label,
  value,
  onChange,
  placeholder = '',
  multiline = false,
  rows = 4,
  maxLength,
  required = false,
  disabled = false,
  helpText,
  className = '',
}) => {
  // Memoize handler to prevent unnecessary re-renders
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      onChange(event.currentTarget.value);
    },
    [onChange],
  );

  const inputId = useMemo(() => `text-editor-${label.replace(/\s+/g, '-').toLowerCase()}`, [label]);

  return (
    <div className={`af-property-editor__group ${className}`}>
      <label htmlFor={inputId} className="af-property-editor__label">
        {label}
        {required && <span className="af-property-editor__required">*</span>}
      </label>

      {multiline ? (
        <textarea
          id={inputId}
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          rows={rows}
          maxLength={maxLength}
          disabled={disabled}
          className="af-property-editor__textarea"
          aria-label={label}
          aria-required={required}
          aria-describedby={helpText ? `${inputId}-help` : undefined}
        />
      ) : (
        <input
          id={inputId}
          type="text"
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          maxLength={maxLength}
          disabled={disabled}
          required={required}
          className="af-property-editor__input"
          aria-label={label}
          aria-required={required}
          aria-describedby={helpText ? `${inputId}-help` : undefined}
        />
      )}

      {helpText && (
        <div id={`${inputId}-help`} className="af-property-editor__help">
          {helpText}
        </div>
      )}

      {maxLength && value.length > 0 && (
        <div className="af-property-editor__counter">
          {value.length}/{maxLength}
        </div>
      )}
    </div>
  );
};

TextPropertyEditor.displayName = 'TextPropertyEditor';
